import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../api/axiosConfig';
import { WEEKDAYS, isoWeek, parseISODate, todayISO } from '../utils/dates';
import SalePickerRows from '../components/SalePickerRows';
import { formatBoxes43, formatRatio, summarizeHarvestSales } from '../utils/harvestSales';
import '../styles/rowActions.css';
import './SalesFormPage.css';

// Observaciones counts, in sheet order. Keys match HARVEST_DEFECT_FIELDS in backend/core/models.py.
const DEFECTS = [
  { key: 'mutant', label: 'MUTANTE' },
  { key: 'over_grade', label: 'SOBRE GRADO' },
  { key: 'altered', label: 'ALTERADO' },
  { key: 'poor', label: 'POBRE' },
  { key: 'erwinia', label: 'ERWIÑIA' },
  { key: 'under_grade', label: 'BAJO GRADO' },
  { key: 'sooty_mold', label: 'FUMAGINA' },
  { key: 'chlorotic', label: 'CLORÓTICOS' },
  { key: 'mealybug', label: 'COCHINILLA' },
  { key: 'fallen', label: 'CAÍDO' },
  { key: 'cream', label: 'CREMA' },
  { key: 'virosis', label: 'VIROSIS' },
  { key: 'red_spot', label: 'MANCHA ROJA' },
  { key: 'explosive', label: 'EXPLOSIVOS' },
];

let nextRibbonKey = 1;
const blankRibbonRow = () => ({ key: nextRibbonKey++, ribbon: '', age: '', quantity: '' });

const blankForm = (date) => ({
  date,
  sales: [], // sale ids (strings), in the order they were added
  saleBoxes: {}, // cajas per sale id (strings): pre-filled from the sale, editable
  // Empresa and Hacienda: follow the first sale's company and farm until the user picks others
  // (a farm of another company too).
  company: '',
  companyEdited: false,
  farm: '',
  farmEdited: false,
  ...Object.fromEntries(DEFECTS.map((d) => [d.key, ''])),
  lots_harvested: '',
  lots_flowered: '',
  ribbons: [],
});

const toIntOrNull = (v) => (v === '' ? null : Number(v));

export default function HarvestFormPage() {
  const { id: harvestId } = useParams();
  const isEdit = Boolean(harvestId);
  const navigate = useNavigate();
  const [sales, setSales] = useState([]);
  const [ribbons, setRibbons] = useState([]);
  const [farms, setFarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(() => blankForm(todayISO()));
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('harvest-details/available-sales/'), api.get('ribbons/'), api.get('farms/'),
      isEdit ? api.get(`harvest-details/${harvestId}/`) : Promise.resolve(null),
    ])
      .then(([s, r, f, h]) => {
        setSales(s.data);
        setRibbons(r.data);
        setFarms(f.data);
        if (h) {
          const hd = h.data;
          const str = (v) => (v == null ? '' : String(v));
          setForm({
            date: hd.date,
            sales: hd.sales.map(String),
            saleBoxes: Object.fromEntries(hd.sales_detail.map((s) => [String(s.id), str(s.harvest_boxes)])),
            company: hd.farm_company == null ? '' : String(hd.farm_company),
            companyEdited: hd.farm_company != null && hd.farm_company !== hd.sales_detail[0]?.company,
            farm: hd.farm == null ? '' : String(hd.farm),
            farmEdited: hd.farm != null && hd.farm !== hd.sales_detail[0]?.farm,
            ...Object.fromEntries(DEFECTS.map((d) => [d.key, str(hd[d.key])])),
            lots_harvested: str(hd.lots_harvested),
            lots_flowered: str(hd.lots_flowered),
            ribbons: hd.ribbons.map((rb) => ({
              key: nextRibbonKey++, ribbon: String(rb.ribbon), age: str(rb.age), quantity: str(rb.quantity),
            })),
          });
        }
      })
      .catch(() => setStatus({
        type: 'error', text: isEdit ? 'No se pudieron cargar los detalles de cosecha.' : 'No se pudieron cargar las listas.',
      }))
      .finally(() => setLoading(false));
  }, [isEdit, harvestId]);

  const selectedDate = form.date ? parseISODate(form.date) : null;
  const selectedSales = form.sales
    .map((id) => sales.find((s) => String(s.id) === id))
    .filter(Boolean)
    .map((s) => {
      const typed = form.saleBoxes[String(s.id)];
      return { ...s, harvest_boxes: typed === '' || typed == null ? null : Number(typed) };
    });
  const summary = selectedSales.length ? summarizeHarvestSales(selectedSales) : null;
  const totalBoxes43 = summary ? summary.boxes.reduce((sum, b) => sum + (b.value43 || 0), 0) : null;
  const addSale = (saleId) => {
    const picked = sales.find((s) => String(s.id) === saleId);
    if (!picked) return;
    setForm((fm) => (fm.sales.includes(saleId) ? fm : {
      ...fm,
      sales: [...fm.sales, saleId],
      saleBoxes: { ...fm.saleBoxes, [saleId]: String(picked.boxes) },
    }));
  };
  const removeSale = (saleId) => setForm((fm) => {
    const { [saleId]: _removed, ...saleBoxes } = fm.saleBoxes;
    return { ...fm, sales: fm.sales.filter((id) => id !== saleId), saleBoxes };
  });
  const setSaleBoxes = (saleId, value) => setForm((fm) => ({
    ...fm, saleBoxes: { ...fm.saleBoxes, [saleId]: value.replace(/\D/g, '') },
  }));

  // Calculated live, the same way the API saves them (HarvestDetail.recalculate()):
  // cosechados = sum of the cintas; procesados = cosechados − rechazados;
  // total cajas (43lbs) = sum of every sale's cajas (43lbs); ratio = total ÷ procesados.
  const rejected = DEFECTS.reduce((sum, d) => sum + (Number(form[d.key]) || 0), 0);
  const harvested = form.ribbons.reduce((sum, r) => sum + (Number(r.quantity) || 0), 0);
  const processed = harvested - rejected;
  const ratio = totalBoxes43 != null && processed > 0 ? totalBoxes43 / processed : null;

  const setDigits = (field) => (e) => setForm((fm) => ({ ...fm, [field]: e.target.value.replace(/\D/g, '') }));

  const setRibbonRow = (key, field, value) => setForm((fm) => ({
    ...fm,
    ribbons: fm.ribbons.map((r) => (r.key === key ? { ...r, [field]: value } : r)),
  }));
  const addRibbonRow = () => setForm((fm) => ({ ...fm, ribbons: [...fm.ribbons, blankRibbonRow()] }));
  const removeRibbonRow = (key) => setForm((fm) => ({ ...fm, ribbons: fm.ribbons.filter((r) => r.key !== key) }));

  const ribbonsComplete = form.ribbons.every((r) => r.ribbon && r.age !== '' && r.quantity !== '');
  const boxesComplete = form.sales.every((id) => (form.saleBoxes[id] ?? '') !== '');
  // Empresa: any company with farms the user sees, pre-selected with the first sale's; Hacienda: that
  // company's farms, pre-selected with the first sale's farm (or the company's first farm).
  const companyOptions = [...new Map(farms.map((f) => [String(f.company), f.company_name]))]
    .map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  const firstSale = selectedSales[0];
  const companyId = form.companyEdited && companyOptions.some((c) => c.id === form.company)
    ? form.company : String(firstSale?.company ?? '');
  const farmOptions = farms.filter((f) => String(f.company) === companyId);
  let farmId = '';
  if (form.farmEdited && farmOptions.some((f) => String(f.id) === form.farm)) farmId = form.farm;
  else if (firstSale && String(firstSale.company) === companyId) farmId = String(firstSale.farm);
  else if (farmOptions.length) farmId = String(farmOptions[0].id);
  const isComplete = form.date && form.sales.length > 0 && boxesComplete && ribbonsComplete && farmId;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isComplete) return;
    setSaving(true);
    setStatus(null);
    try {
      const payload = {
        date: form.date,
        sales: form.sales.map(Number),
        sale_boxes: Object.fromEntries(form.sales.map((id) => [id, Number(form.saleBoxes[id])])),
        ...Object.fromEntries(DEFECTS.map((d) => [d.key, toIntOrNull(form[d.key])])),
        farm: Number(farmId),
        lots_harvested: form.lots_harvested.trim(),
        lots_flowered: form.lots_flowered.trim(),
        ribbons: form.ribbons.map((r) => ({ ribbon: Number(r.ribbon), age: Number(r.age), quantity: Number(r.quantity) })),
      };
      if (isEdit) {
        await api.patch(`harvest-details/${harvestId}/`, payload);
        navigate('/cosecha/historial');
        return;
      }
      await api.post('harvest-details/', payload);
      setStatus({ type: 'success', text: 'Detalles de cosecha registrados correctamente.' });
      // Keep the date: several sheets are usually entered for the same day.
      setForm((fm) => blankForm(fm.date));
    } catch (err) {
      const data = err.response?.data;
      setStatus({
        type: 'error',
        text: data?.detail || data?.sales?.[0] || data?.farm?.[0] || 'No se pudieron guardar los detalles de cosecha.',
      });
    } finally {
      setSaving(false);
    }
  };

  const row = (label, content, className = '') => (
    <tr key={label}>
      <td className="sales-label">{label}</td>
      <td className={`sales-value ${className}`}>{content}</td>
    </tr>
  );
  const section = (label) => (
    <tr key={label}><td colSpan={2} className="harvest-section">{label}</td></tr>
  );
  const digitsInput = (field) => (
    <input className="sales-cell-input" inputMode="numeric" value={form[field]} onChange={setDigits(field)} />
  );
  const textInput = (field) => (
    <input
      className="sales-cell-input"
      maxLength={255}
      value={form[field]}
      onChange={(e) => setForm((fm) => ({ ...fm, [field]: e.target.value }))}
    />
  );
  const fromSale = (value) => (summary ? (value || <span className="sales-muted">—</span>) : '');

  return (
    <div className="sales-form-root">
      <div className="sales-form-topbar">
        <Link to="/production" className="sales-back-link">← Producción</Link>
        <Link to="/cosecha/historial" className="sales-back-link">Historial de Cosecha →</Link>
      </div>

      {status && <div className={`sales-status sales-status-${status.type}`}>{status.text}</div>}

      {loading ? (
        <div className="sales-empty">Cargando…</div>
      ) : (
        <form onSubmit={handleSubmit}>
          <table className="sales-sheet remision-sheet harvest-sheet">
            <thead>
              <tr><th colSpan={2} className="sales-sheet-header harvest-header">DATOS DE COSECHA</th></tr>
            </thead>
            <tbody>
              {row('FECHA', (
                <input
                  type="date"
                  className="sales-cell-input"
                  value={form.date}
                  onChange={(e) => setForm((fm) => ({ ...fm, date: e.target.value }))}
                  required
                />
              ))}
              {row('SEMANA', selectedDate ? isoWeek(selectedDate) : '')}
              {row('DIA', selectedDate ? WEEKDAYS[selectedDate.getDay()] : '')}
              {selectedSales.map((s, i) => row(`VENTA ${i + 1}`, (
                <div className="harvest-sale-item">
                  <span>#{s.id} · {s.farm_name} · {s.exporter_name} · {s.boxes.toLocaleString('es-EC')} cajas</span>
                  <button
                    type="button"
                    className="row-icon-btn"
                    onClick={() => removeSale(String(s.id))}
                    title="Quitar venta"
                    aria-label={`Quitar venta ${s.id}`}
                  >
                    🗑️
                  </button>
                </div>
              ), 'remision-auto'))}
              <SalePickerRows
                sales={sales}
                value=""
                onSelect={addSale}
                date={form.date}
                label={selectedSales.length ? 'AGREGAR VENTA' : 'ID DE VENTA'}
                exclude={form.sales}
              />
              {row('EMPRESA', (
                <select
                  className="sales-cell-input"
                  value={selectedSales.length ? companyId : ''}
                  onChange={(e) => setForm((fm) => ({ ...fm, company: e.target.value, companyEdited: true, farm: '', farmEdited: false }))}
                  disabled={!selectedSales.length}
                  title="Automático desde la venta; se puede cambiar para elegir una hacienda de otra empresa"
                  aria-label="Empresa"
                >
                  {!selectedSales.length && <option value="">Elija una venta primero</option>}
                  {selectedSales.length > 0 && companyOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              ), 'sales-manual')}
              {row('HACIENDA', (
                <select
                  className="sales-cell-input"
                  value={farmId}
                  onChange={(e) => setForm((fm) => ({ ...fm, farm: e.target.value, farmEdited: true }))}
                  disabled={!selectedSales.length || !farmOptions.length}
                  title="Automático desde la venta; se puede cambiar por otra finca de la empresa"
                  aria-label="Hacienda"
                >
                  {!selectedSales.length && <option value="">Elija una venta primero</option>}
                  {selectedSales.length > 0 && farmOptions.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              ), 'sales-manual')}
              {row('EXPORTADORA', fromSale(summary?.exporters), 'remision-auto')}
              {row('MARCA DE CAJA', fromSale(summary?.brands), 'remision-auto')}

              {section('OBSERVACIONES')}
              {DEFECTS.map((d) => row(d.label, digitsInput(d.key), 'sales-manual'))}
              {row('RACIMOS RECHAZADOS', rejected, 'remision-auto harvest-total')}
              {row('RACIMOS COSECHADOS', harvested, 'remision-auto harvest-total')}
              {row('RACIMOS PROCESADOS', processed, 'remision-auto harvest-total')}
              {row('RATIO', ratio == null ? '' : formatRatio(ratio), 'remision-auto harvest-total')}
              {/* Per sale: cajas (editable), peso de caja, cajas (43lbs). */}
              {summary
                ? summary.boxes.flatMap((b, i) => [
                  row(b.label, (
                    <input
                      className="sales-cell-input"
                      inputMode="numeric"
                      value={form.saleBoxes[String(b.saleId)] ?? ''}
                      onChange={(e) => setSaleBoxes(String(b.saleId), e.target.value)}
                      title="Automático desde la venta; se puede editar"
                    />
                  ), 'sales-manual'),
                  row(summary.fruitWeights[i].label, summary.fruitWeights[i].text, 'remision-auto'),
                  row(b.label43, formatBoxes43(b.value43), 'remision-auto'),
                ])
                : ['CAJAS', 'PESO DE CAJA', 'CAJAS (43LBS)'].map((label) => row(label, '', 'remision-auto'))}
              {row('TOTAL CAJAS (43LBS)', formatBoxes43(totalBoxes43), 'remision-auto harvest-total')}

              {section('TOTAL DE CINTA')}
              <tr>
                <td colSpan={2} className="harvest-ribbons-cell">
                  <table className="harvest-ribbons">
                    <thead>
                      <tr><th>COLOR</th><th>EDAD</th><th>CANTIDAD</th><th aria-label="Acciones" /></tr>
                    </thead>
                    <tbody>
                      {form.ribbons.map((r) => {
                        const picked = ribbons.find((rb) => String(rb.id) === r.ribbon);
                        return (
                          <tr key={r.key}>
                            <td>
                              <div className="harvest-ribbon-color">
                                <span className="harvest-swatch" style={{ background: picked?.hex_color || 'transparent' }} />
                                <select
                                  className="sales-cell-input"
                                  value={r.ribbon}
                                  onChange={(e) => setRibbonRow(r.key, 'ribbon', e.target.value)}
                                >
                                  <option value="">— cinta —</option>
                                  {ribbons.map((rb) => <option key={rb.id} value={rb.id}>{rb.color}</option>)}
                                </select>
                              </div>
                            </td>
                            <td className="sales-manual">
                              <input
                                className="sales-cell-input"
                                inputMode="numeric"
                                value={r.age}
                                onChange={(e) => setRibbonRow(r.key, 'age', e.target.value.replace(/\D/g, ''))}
                                aria-label="Edad"
                              />
                            </td>
                            <td className="sales-manual">
                              <input
                                className="sales-cell-input"
                                inputMode="numeric"
                                value={r.quantity}
                                onChange={(e) => setRibbonRow(r.key, 'quantity', e.target.value.replace(/\D/g, ''))}
                                aria-label="Cantidad"
                              />
                            </td>
                            <td className="harvest-ribbon-actions">
                              <button
                                type="button"
                                className="row-icon-btn"
                                onClick={() => removeRibbonRow(r.key)}
                                title="Quitar cinta"
                                aria-label="Quitar cinta"
                              >
                                🗑️
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <button type="button" className="harvest-add-ribbon" onClick={addRibbonRow}>+ Agregar cinta</button>
                </td>
              </tr>

              {row('LOTES COSECHADOS', textInput('lots_harvested'), 'sales-manual')}
              {row('LOTES FLORIADOS', textInput('lots_flowered'), 'sales-manual')}
            </tbody>
          </table>

          <div className="sales-actions">
            {isEdit && (
              <Link to="/cosecha/historial" className="sales-cancel-btn">Cancelar</Link>
            )}
            <button className="sales-save-btn" type="submit" disabled={saving || !isComplete}>
              {saving ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Guardar detalles de cosecha'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
